import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

export default function ConfigurationsList() {
  const [configurations, setConfigurations] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetchConfigurations()
  }, [search])

  const fetchConfigurations = () => {
    setLoading(true)
    let url = '/api/configurations'
    if (search) url += `?search=${encodeURIComponent(search)}`

    fetch(url)
      .then(response => response.json())
      .then(data => {
        setConfigurations(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error fetching configurations:', err)
        setLoading(false)
      })
  }

  const deleteConfiguration = (id) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return

    fetch(`/api/configurations/${id}`, { method: 'DELETE' })
      .then(response => {
        if (response.ok) {
          setConfigurations(configurations.filter(c => c.id !== id))
        } else {
          return response.json().then(data => {
            alert(data.errors?.join(', ') || 'Failed to delete configuration')
          })
        }
      })
      .catch(err => console.error('Error deleting configuration:', err))
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  return (
    <div className="px-4 sm:px-0">
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Configurations</h1>
          <p className="mt-2 text-sm text-gray-600">
            Base build templates with predefined parts
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/configurations/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-900 hover:bg-gray-800"
          >
            + Add Configuration
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Search
        </label>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name..."
          className="w-full md:w-1/2 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
        />
      </div>

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading configurations...</div>
          </div>
        ) : configurations.length === 0 ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">No configurations found</div>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parts Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {configurations.map((config) => (
                <tr key={config.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {config.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {config.parts_count} parts
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(config.total_cost)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <Link
                      to={`/configurations/${config.id}/edit`}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => deleteConfiguration(config.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Showing {configurations.length} configuration{configurations.length !== 1 ? 's' : ''}
      </div>
    </div>
  )
}
